import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminPagedQuery, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { BacklogCard } from '../components/BacklogCard'
import { TaskEditor } from '../components/TaskEditor'
import {
  EMPTY_FILTERS,
  TaskFilters,
  toQuery,
  type TaskFilterState,
} from '../components/TaskFilters'
import { TaskListView } from '../components/TaskListView'
import { useAdminContext } from '../session'
import type { Page } from '../useCollection'
import type {
  I18n,
  ProjectCategory,
  ProjectDashboard,
  Person,
  Task,
  TaskPriority,
  TaskStatus,
} from '../types'

type View = 'board' | 'list'

/**
 * L'espace projet — la préparation de la soirée.
 *
 * Commun à toute l'équipe : chacun voit où en est l'ensemble, chacun n'agit
 * que sur ce que ses droits autorisent. L'écran s'ouvre donc sur l'avancement
 * global, pas sur un formulaire de création : la question qu'on se pose en
 * arrivant est « où en est-on ? », pas « qu'est-ce que j'ajoute ? ».
 *
 * Deux vues seulement, et c'est délibéré. Le kanban sert à faire avancer les
 * choses, la liste à les retrouver. Un calendrier de plus n'aurait rien dit
 * que la colonne « échéance » ne dit déjà, triée.
 */
export function AdminProjectPage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()
  const [view, setView] = useState<View>('board')
  // L'identifiant plutôt que l'objet : après un enregistrement, la liste est
  // rechargée et un objet gardé en état afficherait l'ancienne version.
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const [filters, setFilters] = useState<TaskFilterState>(EMPTY_FILTERS)

  useEffect(() => {
    document.title = t('admin.section.project')
  }, [t])

  const label = (value: I18n) =>
    value[i18n.language] || value[Object.keys(value)[0] ?? ''] || '—'

  const base = `/admin/events/${eventId}/project`

  const board = useAdminQuery<ProjectDashboard>(
    ['admin-project-dashboard', eventId, i18n.language],
    `${base}/dashboard?locale=${i18n.language}`,
    eventId,
  )
  const statuses = useAdminQuery<TaskStatus[]>(
    ['admin-task-statuses', eventId], `${base}/statuses`, eventId,
  )
  const priorities = useAdminQuery<TaskPriority[]>(
    ['admin-task-priorities', eventId], `${base}/priorities`, eventId,
  )
  const categories = useAdminQuery<Page<ProjectCategory>>(
    ['admin-project-categories', eventId], `${base}/categories`, eventId,
  )
  // L'annuaire du module projet, pas celui de l'administration : confier une
  // tâche à un collègue ne doit pas exiger le droit d'administrer des comptes,
  // sans quoi chaque chef de poste devrait être administrateur.
  const staff = useAdminQuery<Person[]>(
    ['admin-project-people', eventId], `${base}/people`, eventId,
  )

  const query = useMemo(() => toQuery(filters), [filters])

  // Toutes les pages, pas la première. Le backlog de préparation dépasse
  // quatre cents tâches et le serveur en rend deux cents au maximum : s'arrêter
  // là afficherait la moitié du travail sans le moindre signe.
  const tasks = useAdminPagedQuery<Task>(
    ['admin-tasks', eventId, query],
    (page) => `${base}/tasks?page=${page}&page_size=200${query ? `&${query}` : ''}`,
    eventId,
  )
  const rows = useMemo(() => tasks.data ?? [], [tasks.data])

  const invalidate = [
    ['admin-tasks', eventId],
    ['admin-project-dashboard', eventId, i18n.language],
  ]
  const move = useAdminMutation<Task, { id: string; status_id: string }>(
    'patch', (variables) => `${base}/tasks/${variables.id}`, eventId, invalidate,
  )
  const create = useAdminMutation<Task, { title: string }>(
    'post', () => `${base}/tasks`, eventId, invalidate,
  )
  const save = useAdminMutation<Task, { id: string } & Record<string, unknown>>(
    'patch', (variables) => `${base}/tasks/${variables.id}`, eventId, invalidate,
  )
  const remove = useAdminMutation<{ message: string }, { id: string }>(
    'delete', (variables) => `${base}/tasks/${variables.id}`, eventId, invalidate,
  )
  // La carte disparaît à la seconde du clic, sans attendre le rechargement de
  // la liste. Une ligne qui reste affichée le temps d'un aller-retour donne
  // l'impression que la suppression n'a pas pris — et invite à recliquer.
  const [justRemoved, setJustRemoved] = useState<Set<string>>(new Set())
  const bootstrap = useAdminMutation<{ message: string }, Record<string, never>>(
    'post', () => `${base}/bootstrap`, eventId,
    [['admin-task-statuses', eventId], ['admin-task-priorities', eventId],
     ['admin-project-categories', eventId], ...invalidate],
  )

  const backlog = useAdminMutation<
    { created: number; skipped: number },
    Record<string, never>
  >('post', () => `${base}/backlog`, eventId, [
    ...invalidate,
    ['admin-backlog-preview', eventId ?? ''],
  ])

  const workflow = statuses.data ?? []
  const people = staff.data ?? []
  const names = new Map(people.map((row) => [row.id, row.label]))
  const categoryNames = new Map(
    (categories.data?.items ?? []).map((row) => [row.id, label(row.name_i18n)]),
  )
  const visible = useMemo(
    () => rows.filter((row) => !justRemoved.has(row.id)),
    [rows, justRemoved],
  )
  const openTask = visible.find((row) => row.id === openTaskId) ?? null

  // Une édition sans workflow ne peut pas accueillir de tâche. Plutôt que de
  // laisser un écran vide inexplicable, on propose le geste qui débloque.
  if (statuses.isSuccess && workflow.length === 0) {
    return (
      <>
        <AdminHeader title={t('admin.section.project')} />
        <Card className="max-w-xl">
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.project.setupTitle')}
          </h2>
          <p className="mt-3 text-sm text-content-secondary">
            {t('admin.project.setupBody')}
          </p>
          <Button
            className="mt-5"
            disabled={bootstrap.isPending}
            onClick={() => bootstrap.mutate({})}
          >
            {t('admin.project.setupAction')}
          </Button>
          {bootstrap.isError ? (
            <p className="mt-3 text-sm text-state-danger">{bootstrap.error.message}</p>
          ) : null}
        </Card>
      </>
    )
  }

  return (
    <>
      <AdminHeader
        title={t('admin.section.project')}
        description={t('admin.project.description')}
        action={
          <div className="flex gap-2">
            <Button
              variant={view === 'board' ? 'ember' : 'ghost'}
              size="sm"
              onClick={() => setView('board')}
            >
              {t('admin.project.board')}
            </Button>
            <Button
              variant={view === 'list' ? 'ember' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
            >
              {t('admin.project.list')}
            </Button>
          </div>
        }
      />

      {board.isPending ? <Skeleton className="h-28 w-full" /> : null}
      {board.data ? <Overview board={board.data} /> : null}

      {/* Le backlog livré : proposé tant qu'il reste des tâches à poser, et
          silencieux ensuite. Un bouton qui ne fait plus rien encombre. */}
      <BacklogCard
        eventId={eventId}
        busy={backlog.isPending}
        error={backlog.error ? backlog.error.message : null}
        onApply={() => backlog.mutate({})}
      />

      <div className="mt-6">
        <QuickAdd busy={create.isPending} onCreate={(title) => create.mutate({ title })} />
      </div>

      {/* Les filtres, au-dessus des deux vues : on cherche la même chose
          qu'on soit en kanban ou en liste. */}
      <TaskFilters
        filters={filters}
        onChange={setFilters}
        categories={categories.data?.items ?? []}
        people={people}
        priorities={priorities.data ?? []}
        statuses={workflow}
        label={label}
        total={visible.length}
      />

      {tasks.isPending ? <Skeleton className="h-64 w-full" /> : null}

      {tasks.data && view === 'board' ? (
        <Board
          statuses={workflow}
          tasks={visible}
          label={label}
          names={names}
          busy={move.isPending}
          onMove={(id, status_id) => move.mutate({ id, status_id })}
          onOpen={setOpenTaskId}
        />
      ) : null}

      {tasks.data && view === 'list' ? (
        <TaskListView
          tasks={visible}
          statuses={workflow}
          priorities={priorities.data ?? []}
          categoryNames={categoryNames}
          label={label}
          busy={move.isPending}
          onMove={(id, status_id) => move.mutate({ id, status_id })}
          onOpen={setOpenTaskId}
        />
      ) : null}

      {openTask ? (
        <TaskEditor
          task={openTask}
          statuses={workflow}
          priorities={priorities.data ?? []}
          categories={categories.data?.items ?? []}
          people={people}
          label={label}
          busy={save.isPending || remove.isPending}
          error={
            save.isError
              ? save.error.message
              : remove.isError
                ? remove.error.message
                : null
          }
          onSave={(draft) =>
            save.mutate(
              { id: openTask.id, ...draft },
              { onSuccess: () => setOpenTaskId(null) },
            )
          }
          onDelete={() => {
            const doomed = openTask.id
            setJustRemoved((current) => new Set(current).add(doomed))
            setOpenTaskId(null)
            remove.mutate(
              { id: doomed },
              {
                // Le serveur a refusé : la ligne revient, et l'erreur
                // s'affiche. Une suppression qu'on croit faite alors qu'elle a
                // échoué est pire que pas de suppression du tout.
                onError: () =>
                  setJustRemoved((current) => {
                    const next = new Set(current)
                    next.delete(doomed)
                    return next
                  }),
              },
            )
          }}
          onClose={() => setOpenTaskId(null)}
        />
      ) : null}
    </>
  )
}

/** Les compteurs. Comptés serveur, par nature de statut — jamais par libellé. */
function Overview({ board }: { board: ProjectDashboard }) {
  const { t } = useTranslation()

  const tiles = [
    { label: t('admin.project.progress'), value: `${board.percent} %`, tone: 'champagne' },
    { label: t('admin.project.done'), value: board.done, tone: '' },
    { label: t('admin.project.doing'), value: board.in_progress, tone: '' },
    { label: t('admin.project.late'), value: board.overdue, tone: board.overdue ? 'danger' : '' },
    { label: t('admin.project.blocked'), value: board.blocked, tone: board.blocked ? 'danger' : '' },
    { label: t('admin.project.dueSoon'), value: board.due_soon, tone: '' },
    { label: t('admin.project.unassigned'), value: board.unassigned, tone: '' },
  ]

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="rounded-lg border border-line-subtle bg-bg-elevated px-4 py-3"
          >
            <p className="overline text-content-muted">{tile.label}</p>
            <p
              className={cn(
                'mt-1 font-display text-heading-lg tabular-nums',
                tile.tone === 'champagne' && 'text-champagne-300',
                tile.tone === 'danger' && 'text-state-danger',
                !tile.tone && 'text-content-primary',
              )}
            >
              {tile.value}
            </p>
          </div>
        ))}
      </div>

      {board.by_category.length > 0 ? (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {board.by_category.map((row) => (
            <div key={row.label} className="rounded-lg border border-line-subtle px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-content-primary">{row.label}</span>
                <span className="font-mono text-caption tabular-nums text-content-muted">
                  {row.done}/{row.total}
                </span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-bg-overlay">
                <div
                  className="h-full rounded-full bg-champagne-400"
                  style={{ width: `${row.percent}%` }}
                />
              </div>
              {row.overdue > 0 ? (
                <p className="mt-2 text-caption text-state-danger">
                  {t('admin.project.lateCount', { count: row.overdue })}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </>
  )
}

/**
 * Le kanban.
 *
 * Pas de glisser-déposer : sur un téléphone, dans un couloir, un menu déroulant
 * est plus sûr qu'un geste qu'on rate. La colonne se choisit, elle ne se traîne
 * pas.
 */
function Board({
  statuses,
  tasks,
  label,
  names,
  busy,
  onMove,
  onOpen,
}: {
  statuses: TaskStatus[]
  tasks: Task[]
  label: (value: I18n) => string
  names: Map<string, string>
  busy: boolean
  onMove: (id: string, statusId: string) => void
  onOpen: (id: string) => void
}) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {statuses.map((status) => {
        const column = tasks.filter((task) => task.status_id === status.id)
        return (
          <section key={status.id} className="w-72 shrink-0">
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-sm text-content-primary">
                <span
                  aria-hidden
                  className="h-2 w-2 rounded-full"
                  style={{ background: status.color ?? 'var(--line-strong)' }}
                />
                {label(status.name_i18n)}
              </span>
              <span className="font-mono text-caption tabular-nums text-content-muted">
                {column.length}
              </span>
            </div>

            <div className="space-y-2">
              {column.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  statuses={statuses}
                  label={label}
                  names={names}
                  busy={busy}
                  onMove={onMove}
                  onOpen={onOpen}
                />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

function TaskCard({
  task,
  statuses,
  label,
  names,
  busy,
  onMove,
  onOpen,
}: {
  task: Task
  statuses: TaskStatus[]
  label: (value: I18n) => string
  names: Map<string, string>
  busy: boolean
  onMove: (id: string, statusId: string) => void
  onOpen: (id: string) => void
}) {
  const { t } = useTranslation()
  const owner = task.owner_user_id ? names.get(task.owner_user_id) : undefined
  const helpers = task.assignee_ids
    .map((id) => names.get(id))
    .filter((name): name is string => Boolean(name))

  return (
    <article
      className={cn(
        'rounded-lg border bg-bg-elevated px-3 py-3',
        task.is_overdue ? 'border-state-danger/50' : 'border-line-subtle',
      )}
    >
      {/* Le titre ouvre le panneau : c'est là qu'on clique d'instinct pour
          « voir la tâche », et le sélecteur de colonne reste utilisable sans
          ouvrir quoi que ce soit. */}
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          className="text-left text-sm text-content-primary hover:text-champagne-300"
          onClick={() => onOpen(task.id)}
        >
          {task.title}
        </button>
        <span className="shrink-0 font-mono text-caption text-content-muted">
          {task.reference}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {task.is_overdue ? (
          <Badge tone="ember">{t('admin.project.overdue')}</Badge>
        ) : task.due_at ? (
          <span className="text-caption text-content-muted">
            {new Date(task.due_at).toLocaleDateString()}
          </span>
        ) : null}
        {owner ? (
          <span className="text-caption text-champagne-300">{owner}</span>
        ) : null}
        {helpers.length > 0 ? (
          <span className="text-caption text-content-muted">+ {helpers.join(', ')}</span>
        ) : null}
      </div>

      <select
        aria-label={t('admin.project.moveTo')}
        value={task.status_id}
        disabled={busy}
        onChange={(event) => onMove(task.id, event.target.value)}
        className="mt-3 w-full rounded-md border border-line bg-bg-base px-2 py-1.5 text-caption text-content-secondary"
      >
        {statuses.map((status) => (
          <option key={status.id} value={status.id}>
            {label(status.name_i18n)}
          </option>
        ))}
      </select>
    </article>
  )
}

/** Ajouter une tâche en une frappe : le titre suffit, le reste s'affine après. */
function QuickAdd({ busy, onCreate }: { busy: boolean; onCreate: (title: string) => void }) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')

  return (
    <form
      className="flex flex-1 gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        // `busy` garde la **soumission**, pas seulement le bouton. Le bouton
        // était désactivé pendant l'envoi, mais la touche Entrée soumet le
        // formulaire sans passer par lui : deux frappes rapides créaient deux
        // tâches jumelles. On en supprimait une, et la seconde restait —
        // exactement l'impression d'une suppression annulée.
        if (busy) return
        const trimmed = title.trim()
        if (!trimmed) return
        onCreate(trimmed)
        setTitle('')
      }}
    >
      <input
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder={t('admin.project.quickAdd')}
        className="min-w-0 flex-1 rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
      />
      <Button type="submit" size="sm" variant="outline" disabled={busy || !title.trim()}>
        {t('admin.project.add')}
      </Button>
    </form>
  )
}
